import type { TemplateContext } from "@unbox/api";
import { renderTemplate } from "@unbox/api";

/**
 * Extract the display text of a TEXT message.
 *
 * TikTok wraps TEXT message content in a JSON envelope ('{"content":"..."}'),
 * while activities we build locally contain the plain text directly. Only
 * unwrap when the content is actually that envelope — otherwise render the
 * raw string, never the JSON envelope itself (UNB-1819).
 */
export const extractTextMessageContent = (rawContent: string): string => {
  try {
    const parsed = JSON.parse(rawContent);
    if (parsed && typeof parsed === "object" && typeof parsed.content === "string") {
      return parsed.content;
    }
  } catch {
    // Not JSON — plain-text content
  }
  return rawContent;
};

const HIGHLIGHT_MARKER = "\x00HL_START\x00";
const HIGHLIGHT_END_MARKER = "\x00HL_END\x00";

export function resolveForPreview(template: string, ctx: TemplateContext): React.ReactNode[] {
  const highlight = (value: string) => `${HIGHLIGHT_MARKER}${value}${HIGHLIGHT_END_MARKER}`;
  const resolved = renderTemplate(template, ctx, { format: highlight });
  const markerPattern = new RegExp(`(${HIGHLIGHT_MARKER}[^]*?${HIGHLIGHT_END_MARKER}|\n)`);
  const parts = resolved.split(markerPattern);
  let cursor = 0;
  return parts.map((part) => {
    const key = cursor;
    cursor += part.length + 1;
    if (part === "\n") return <br key={key} />;
    if (part.startsWith(HIGHLIGHT_MARKER)) {
      const value = part.slice(HIGHLIGHT_MARKER.length, -HIGHLIGHT_END_MARKER.length);
      return (
        <span key={key} className="rounded-[3px] bg-brand-100 px-1 text-brand-700">
          {value}
        </span>
      );
    }
    return part;
  });
}
